import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "input",
    "status",
    "suggestionContainer",
    "suggestionBody",
    "trigger",
    "feedbackTrigger",
    "feedbackContainer",
    "feedbackBody",
    "feedbackCalories",
    "feedbackStatus"
  ]
  static values = {
    suggestUrl: String,
    feedbackUrl: String,
    csrfToken: String
  }

  connect() {
    this.currentSuggestion = null
    this.feedbackLoading = false
  }

  requestSuggestion() {
    if (!this.hasSuggestUrlValue) return
    if (this.loading) return

    this.setLoading(true)
    this.showStatus("Generating suggestion…", "info")

    fetch(this.suggestUrlValue, {
      method: "POST",
      headers: this.headers,
      body: JSON.stringify({})
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          const errorMessage = data.error || "Unable to generate a suggestion."
          throw new Error(errorMessage)
        }
        return data
      })
      .then((data) => {
        if (!data.suggestion) {
          throw new Error("OpenAI did not return a suggestion.")
        }
        this.showSuggestion(data.suggestion)
        this.showStatus("Suggestion ready", "success")
      })
      .catch((error) => {
        this.showError(error.message)
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  requestFeedback() {
    if (!this.hasFeedbackUrlValue) return
    if (this.feedbackLoading) return

    const workoutDetails = this.hasInputTarget ? this.inputTarget.value.trim() : ""
    if (workoutDetails.length === 0) {
      this.showFeedbackStatus("Enter your workout details first.", "error")
      return
    }

    this.setFeedbackLoading(true)
    this.showFeedbackStatus("Requesting feedback…", "info")

    fetch(this.feedbackUrlValue, {
      method: "POST",
      headers: this.headers,
      body: JSON.stringify({ exercises: this.inputTarget.value })
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          const errorMessage = data.error || "Unable to generate feedback."
          throw new Error(errorMessage)
        }
        return data
      })
      .then((data) => {
        if (!data.feedback) {
          throw new Error("OpenAI did not return feedback.")
        }
        this.showFeedback(data)
        this.showFeedbackStatus("Feedback ready", "success")
      })
      .catch((error) => {
        this.showFeedbackError(error.message)
      })
      .finally(() => {
        this.setFeedbackLoading(false)
      })
  }

  applySuggestion() {
    if (!this.currentSuggestion || !this.hasInputTarget) return

    const cleanedSuggestion = this.cleanSuggestionForInput(this.currentSuggestion)

    this.inputTarget.value = cleanedSuggestion
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.showStatus("Suggestion copied to the workout.", "success")
  }

  cleanSuggestionForInput(text) {
    const bulletLines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.startsWith("- "))
      .map((line) => line.replace(/^-\s*/, ""))
      .filter((line) => line.length > 0)

    if (bulletLines.length > 0) {
      return bulletLines.join("\n")
    }

    return text.trim()
  }

  showSuggestion(text) {
    this.currentSuggestion = text.trim()
    if (this.hasSuggestionBodyTarget) {
      this.suggestionBodyTarget.textContent = this.currentSuggestion
    }
    if (this.hasSuggestionContainerTarget) {
      this.suggestionContainerTarget.classList.remove("d-none")
    }
  }

  showFeedback(data) {
    const feedbackText = this.formatFeedbackText(data.feedback)
    if (this.hasFeedbackBodyTarget) {
      this.feedbackBodyTarget.textContent = feedbackText
    }

    if (this.hasFeedbackCaloriesTarget) {
      this.feedbackCaloriesTarget.textContent = this.formatCalories(
        data.estimated_calories ?? data.calories
      )
    }

    if (this.hasFeedbackContainerTarget) {
      this.feedbackContainerTarget.classList.remove("d-none")
    }
  }

  showStatus(message, type = "info") {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("text-danger", "text-muted", "text-success")

    switch (type) {
      case "success":
        this.statusTarget.classList.add("text-success")
        break
      case "error":
        this.statusTarget.classList.add("text-danger")
        break
      default:
        this.statusTarget.classList.add("text-muted")
        break
    }
  }

  showError(message) {
    this.showStatus(message, "error")
  }

  showFeedbackStatus(message, type = "info") {
    if (!this.hasFeedbackStatusTarget) return

    this.feedbackStatusTarget.textContent = message
    this.feedbackStatusTarget.classList.remove("text-danger", "text-muted", "text-success")

    switch (type) {
      case "success":
        this.feedbackStatusTarget.classList.add("text-success")
        break
      case "error":
        this.feedbackStatusTarget.classList.add("text-danger")
        break
      default:
        this.feedbackStatusTarget.classList.add("text-muted")
        break
    }
  }

  showFeedbackError(message) {
    this.showFeedbackStatus(message, "error")
  }

  formatFeedbackText(feedback) {
    if (Array.isArray(feedback)) {
      return this.joinFeedbackLines(feedback)
    }

    if (typeof feedback === "string") {
      const trimmed = feedback.trim()
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          const parsed = JSON.parse(trimmed)
          if (Array.isArray(parsed)) {
            return this.joinFeedbackLines(parsed)
          }
        } catch (error) {
          // Ignore JSON parse errors and fall back to the trimmed string
        }
      }
      return trimmed
    }

    if (feedback == null) {
      return ""
    }

    return feedback.toString().trim()
  }

  joinFeedbackLines(lines) {
    return lines
      .map((line) => {
        if (line == null) return ""
        return line.toString().trim()
      })
      .filter((line) => line.length > 0)
      .join("\n")
  }

  setLoading(state) {
    this.loading = state
    if (this.hasTriggerTarget) {
      this.triggerTarget.disabled = state
    }
  }

  setFeedbackLoading(state) {
    this.feedbackLoading = state
    if (this.hasFeedbackTriggerTarget) {
      this.feedbackTriggerTarget.disabled = state
    }
  }

  formatCalories(value) {
    if (value === null || value === undefined) {
      return "—"
    }

    const parsed = Number(value)
    if (Number.isFinite(parsed) && parsed > 0) {
      return `${Math.round(parsed)} kcal`
    }

    if (typeof value === "string") {
      const match = value.match(/\d+/)
      if (match) {
        const fromString = Number(match[0])
        if (Number.isFinite(fromString) && fromString > 0) {
          return `${Math.round(fromString)} kcal`
        }
      }
    }

    return "—"
  }

  get headers() {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json"
    }

    if (this.hasCsrfTokenValue && this.csrfTokenValue) {
      headers["X-CSRF-Token"] = this.csrfTokenValue
    }

    return headers
  }
}
